/**
 * MechanicProductivityTile — iter134
 * ────────────────────────────────────────────────────────────────────────
 * 4-card productivity snapshot for the mechanic home dashboard.
 *
 *   ┌──────────┬──────────┬──────────┬──────────┐
 *   │  Today   │Yesterday │This week │Last week │
 *   │ 5.5/7.5  │  8.0/7.5 │ 38/37.5  │ 36.5/37.5│
 *   │   73%    │   107%   │   101%   │    97%   │
 *   └──────────┴──────────┴──────────┴──────────┘
 *
 * Actual = time_log on JCs in sm_review or completed.
 * Allocated = quoted_labour_hours on JCs in active workshop states.
 * Privacy-gated server-side: mechanics see only their own.
 *
 * Auto-refreshes every 60s.
 */
import { useEffect, useState, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { TrendingUp, TrendingDown, Activity, Calendar, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function fmtHrs(h) {
  if (h == null || h === 0) return '0';
  const n = Number(h);
  return n % 1 === 0 ? n.toFixed(0) : n.toFixed(1);
}

function tierFor(actual, allocated) {
  if (!allocated || allocated <= 0) {
    return actual > 0 ? 'slate' : 'empty';
  }
  const pct = (actual / allocated) * 100;
  if (pct >= 95) return 'emerald';
  if (pct >= 60) return 'amber';
  return 'rose';
}

const TIER_STYLES = {
  emerald: {
    card: 'border-emerald-300 bg-gradient-to-br from-emerald-50 to-white',
    icon: 'bg-emerald-200 text-emerald-700',
    actual: 'text-emerald-700',
    bar: 'bg-emerald-500',
    pct: 'text-emerald-700',
    pctLabel: 'On target',
  },
  amber: {
    card: 'border-amber-300 bg-gradient-to-br from-amber-50 to-white',
    icon: 'bg-amber-200 text-amber-700',
    actual: 'text-amber-700',
    bar: 'bg-amber-500',
    pct: 'text-amber-700',
    pctLabel: 'Below target',
  },
  rose: {
    card: 'border-rose-300 bg-gradient-to-br from-rose-50 to-white',
    icon: 'bg-rose-200 text-rose-700',
    actual: 'text-rose-700',
    bar: 'bg-rose-500',
    pct: 'text-rose-700',
    pctLabel: 'Low',
  },
  slate: {
    card: 'border-slate-300 bg-gradient-to-br from-slate-50 to-white',
    icon: 'bg-slate-200 text-slate-600',
    actual: 'text-slate-700',
    bar: 'bg-slate-400',
    pct: 'text-slate-500',
    pctLabel: 'No alloc',
  },
  empty: {
    card: 'border-slate-200 bg-white',
    icon: 'bg-slate-100 text-slate-400',
    actual: 'text-slate-300',
    bar: 'bg-slate-200',
    pct: 'text-slate-400',
    pctLabel: 'No data',
  },
};

function PeriodCard({ bucket, accentIcon, isCurrent = false }) {
  const t = tierFor(bucket.actual_hours, bucket.allocated_hours);
  const s = TIER_STYLES[t];
  const widthPct = Math.min(150, bucket.allocated_hours > 0
    ? (bucket.actual_hours / bucket.allocated_hours) * 100
    : 0);
  const delta = bucket.delta_hours;
  // iter154 — new fields from productivity-summary. Fall back to 0
  // (older cached responses won't have them, and we don't want NaN in
  // the UI while the deploy propagates).
  const sectionCompleted = Number(bucket.section_completed_hours || 0);
  const produced = Number(bucket.produced_hours != null ? bucket.produced_hours : bucket.actual_hours || 0);
  const timeSaved = Number(bucket.time_saved_hours || (sectionCompleted - produced) || 0);
  const timeSavedPositive = timeSaved > 0.05;
  const timeSavedNegative = timeSaved < -0.05;

  return (
    <Card className={`relative overflow-hidden border-2 ${s.card} p-4`} data-testid={`prod-card-${bucket.period.toLowerCase().replace(/\s+/g, '-')}`}>
      {isCurrent && (
        <div className="absolute top-1 right-1 px-1.5 py-0.5 bg-blue-500 text-white text-[8px] uppercase tracking-wider font-extrabold rounded-full">
          Live
        </div>
      )}
      <div className="flex items-center justify-between mb-2">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${s.icon}`}>
          {accentIcon}
        </div>
        <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
          {bucket.period}
        </span>
      </div>

      {/* Actual / Allocated big number */}
      <div className="flex items-baseline gap-1">
        <span className={`text-3xl font-black tabular-nums ${s.actual}`} data-testid={`prod-actual-${bucket.period.toLowerCase().replace(/\s+/g, '-')}`}>
          {fmtHrs(bucket.actual_hours)}
        </span>
        <span className="text-slate-300 font-light text-xl">/</span>
        <span className="text-slate-500 font-semibold tabular-nums text-base">
          {fmtHrs(bucket.allocated_hours)}h
        </span>
      </div>

      {/* Progress bar */}
      <div className="mt-2 h-1.5 rounded-full bg-slate-100 overflow-hidden relative">
        <div
          className={`h-full ${s.bar} transition-all`}
          style={{ width: `${Math.min(100, widthPct)}%` }}
        />
        {widthPct > 100 && (
          <div className="absolute inset-0 bg-emerald-700/30 animate-pulse" />
        )}
      </div>

      {/* Efficiency + delta + JC count */}
      <div className="mt-2 flex items-center justify-between text-xs">
        <div className="flex items-center gap-1.5">
          <span className={`font-extrabold tabular-nums ${s.pct}`} data-testid={`prod-eff-${bucket.period.toLowerCase().replace(/\s+/g, '-')}`}>
            {bucket.allocated_hours > 0 ? `${Math.round(bucket.efficiency_pct)}%` : '—'}
          </span>
          <span className="text-[9px] uppercase tracking-wider text-slate-400 font-bold">
            {s.pctLabel}
          </span>
        </div>
        {bucket.allocated_hours > 0 && (
          <span className={`inline-flex items-center gap-0.5 text-[10px] font-bold ${
            delta >= 0 ? 'text-emerald-600' : 'text-rose-600'
          }`}>
            {delta >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
            {delta > 0 ? '+' : ''}{fmtHrs(delta)}h
          </span>
        )}
      </div>

      {/* iter154 — Section Completed + Produced + Time Saved mini-strip.
          Section Completed = allocated-hours of TASKS closed in the
          period (proportional split across mechanics who worked on
          shared tasks). Produced = clocked session time. Time Saved
          = Section Completed − Produced (positive = profit, shown
          green; negative = extra effort, shown amber — NEVER red so
          the mechanic isn't shamed for slow days). */}
      <div className="mt-3 grid grid-cols-3 gap-1.5 text-[10px]">
        <div className="rounded-md bg-sky-50 border border-sky-200 px-1.5 py-1 text-center" data-testid={`prod-section-completed-${bucket.period.toLowerCase().replace(/\s+/g, '-')}`}>
          <p className="text-[9px] uppercase tracking-wider text-sky-700 font-bold">Section done</p>
          <p className="text-sm font-black text-sky-900 tabular-nums">{fmtHrs(sectionCompleted)}h</p>
        </div>
        <div className="rounded-md bg-indigo-50 border border-indigo-200 px-1.5 py-1 text-center" data-testid={`prod-produced-${bucket.period.toLowerCase().replace(/\s+/g, '-')}`}>
          <p className="text-[9px] uppercase tracking-wider text-indigo-700 font-bold">Produced</p>
          <p className="text-sm font-black text-indigo-900 tabular-nums">{fmtHrs(produced)}h</p>
        </div>
        <div
          className={`rounded-md px-1.5 py-1 text-center border ${
            timeSavedPositive
              ? 'bg-emerald-50 border-emerald-200'
              : timeSavedNegative
                ? 'bg-amber-50 border-amber-200'
                : 'bg-slate-50 border-slate-200'
          }`}
          data-testid={`prod-time-saved-${bucket.period.toLowerCase().replace(/\s+/g, '-')}`}
        >
          <p className={`text-[9px] uppercase tracking-wider font-bold ${
            timeSavedPositive ? 'text-emerald-700' : timeSavedNegative ? 'text-amber-700' : 'text-slate-600'
          }`}>
            {timeSavedPositive ? 'Saved' : timeSavedNegative ? 'Extra' : 'On par'}
          </p>
          <p className={`text-sm font-black tabular-nums ${
            timeSavedPositive ? 'text-emerald-900' : timeSavedNegative ? 'text-amber-900' : 'text-slate-800'
          }`}>
            {timeSavedPositive ? '+' : ''}{fmtHrs(Math.abs(timeSaved))}h
          </p>
        </div>
      </div>

      {bucket.jc_count > 0 && (
        <p className="mt-2 text-[10px] text-slate-500">
          {bucket.jc_count} JC{bucket.jc_count !== 1 ? 's' : ''} completed / in review
        </p>
      )}
    </Card>
  );
}

export default function MechanicProductivityTile() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/mechanic/productivity-summary`, { credentials: 'include' });
      if (!r.ok) {
        if (r.status === 403) {
          setError('forbidden');
          setLoading(false);
          return;
        }
        throw new Error(`HTTP ${r.status}`);
      }
      setSummary(await r.json());
      setError(null);
    } catch (_e) {
      setError('network');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    // iter140 — User intent: "when mechanic logs in his ipad should be
    // reflecting every 5sec so he gets live update of allocated and
    // produced hours." Bump from 60s → 5s. Still gated on
    // !document.hidden so a backgrounded tab doesn't hammer the backend.
    const id = setInterval(() => { if (!document.hidden) load(); }, 5000);
    return () => clearInterval(id);
  }, [load]);

  if (error === 'forbidden') return null; // non-mechanic viewer — silently hide

  if (loading) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-4 animate-pulse" data-testid="mech-prod-tile-loading">
        <div className="h-3 w-32 bg-slate-200 rounded mb-3"></div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[1,2,3,4].map(i => <div key={i} className="h-28 bg-slate-100 rounded-lg"></div>)}
        </div>
      </div>
    );
  }

  if (!summary || error === 'network') {
    return null;
  }

  // Weekly headline: actual / allocated this week
  const tw = summary.this_week;
  const lw = summary.last_week;
  const weekDelta = tw.actual_hours - lw.actual_hours;
  const headlineTier = tierFor(tw.actual_hours, tw.allocated_hours);
  const headlineStyle = TIER_STYLES[headlineTier];

  return (
    <div className="space-y-3" data-testid="mech-prod-tile">
      {/* Headline strip — weekly */}
      <div className={`rounded-xl border-2 ${headlineStyle.card} p-4 flex items-center gap-4 flex-wrap`}>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${headlineStyle.icon}`}>
          <Activity className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
            Your week so far
          </p>
          <div className="flex items-baseline gap-1.5 flex-wrap">
            <span className={`text-2xl font-black tabular-nums ${headlineStyle.actual}`} data-testid="prod-week-actual">
              {fmtHrs(tw.actual_hours)}h
            </span>
            <span className="text-slate-400 text-base">/</span>
            <span className="text-slate-600 font-semibold tabular-nums">
              {fmtHrs(tw.allocated_hours)}h allocated
            </span>
            {tw.allocated_hours > 0 && (
              <span className={`px-2 py-0.5 rounded-full text-xs font-bold tabular-nums ${headlineStyle.icon}`}>
                {Math.round(tw.efficiency_pct)}%
              </span>
            )}
          </div>
        </div>
        <div className="text-right">
          {lw.actual_hours > 0 && (
            <p className={`text-xs font-semibold inline-flex items-center gap-1 ${
              weekDelta >= 0 ? 'text-emerald-700' : 'text-rose-700'
            }`}>
              {weekDelta >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
              {weekDelta > 0 ? '+' : ''}{fmtHrs(weekDelta)}h vs last week
            </p>
          )}
          <p className="text-[10px] text-slate-500 italic mt-0.5">
            Mon {new Date(tw.start_date + 'T00:00:00').toLocaleDateString('en-AU', { day: '2-digit', month: 'short' })} – Sun {new Date(tw.end_date + 'T00:00:00').toLocaleDateString('en-AU', { day: '2-digit', month: 'short' })}
          </p>
        </div>
      </div>

      {/* 4 period cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="prod-period-cards">
        <PeriodCard bucket={summary.today} accentIcon={<Calendar className="h-4 w-4" />} isCurrent />
        <PeriodCard bucket={summary.yesterday} accentIcon={<Calendar className="h-4 w-4" />} />
        <PeriodCard bucket={summary.this_week} accentIcon={<Activity className="h-4 w-4" />} />
        <PeriodCard bucket={summary.last_week} accentIcon={<Activity className="h-4 w-4" />} />
      </div>

      <p className="text-[10px] text-slate-500 italic flex items-center gap-1 pl-1">
        <AlertCircle className="h-3 w-3" />
        Section done = quote-hours of TASKS you closed. Produced = clocked
        session time. Saved = Section done minus Produced (positive =
        profit for the workshop).
      </p>
    </div>
  );
}
